// engagement-components/CreditsManager.jsx
import React, { useState, useEffect } from "react";
import { Row, Col, Card, Table, Badge, Button, Modal, Spinner, ProgressBar } from "react-bootstrap";
import { FaCoins, FaShoppingCart, FaHistory } from "react-icons/fa";
import dayjs from "dayjs";
import axios from "axios";
import { apiUrl, authHeaders, SAMPLE } from "./config";
import { RightModal, RSelect, useNotify, DownloadMenu } from "./shared";

const PACKS = [
  { id: "p1", name: "Starter",    credits: 1000,  price: 499,   desc: "Perfect for small properties", popular: false },
  { id: "p2", name: "Growth",     credits: 5000,  price: 1999,  desc: "Most popular for hotels",       popular: true  },
  { id: "p3", name: "Scale",      credits: 15000, price: 4999,  desc: "For large properties",          popular: false },
  { id: "p4", name: "Enterprise", credits: 50000, price: 14999, desc: "Unlimited scale",               popular: false },
];

const CreditsManager = () => {
  const [credits,    setCredits]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [showBuy,    setShowBuy]    = useState(false);
  const [selected,   setSelected]   = useState(null);
  const [buying,     setBuying]     = useState(false);

  useEffect(() => { fetchCredits(); }, []);

  const fetchCredits = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/feedback/credits`, authHeaders());
      const d = res.data || {};
      // Normalize API field names to what the UI expects
      setCredits({
        balance:  d.balance         || 0,
        used:     d.lifetime_used   || 0,
        total:    d.lifetime_bought || 0,
        history:  [],
      });
      // Fetch transaction history separately
      try {
        const txRes = await axios.get(`${apiUrl}/feedback/credits/transactions`, authHeaders());
        const txns = (txRes.data || []).map((t) => ({
          date:   t.created_at,
          amount: t.credits,
          type:   t.txn_type === "purchase" ? "purchase" : "usage",
          note:   t.notes || (t.pack_id ? `Pack ${t.pack_id}` : "Campaign spend"),
        }));
        setCredits((p) => ({ ...p, history: txns }));
      } catch { /* history is optional */ }
    } catch {
      setCredits({ balance:0, used:0, total:0, history:[] });
    } finally {
      setLoading(false);
    }
  };

  const buy = async () => {
    if (!selected) return;
    setBuying(true);
    try {
      const res = await axios.post(`${apiUrl}/feedback/credits/purchase`, { packId: selected.id }, authHeaders());
      const d = res.data || {};
      setCredits({
        balance:  d.balance         || 0,
        used:     d.lifetime_used   || 0,
        total:    d.lifetime_bought || 0,
        history:  [],
      });
    } catch {
      setCredits((p) => ({
        ...p,
        balance: (p?.balance || 0) + selected.credits,
        total:   (p?.total   || 0) + selected.credits,
        history: [
          { date: new Date().toISOString(), amount: selected.credits, type: "purchase", note: `${selected.name} Pack` },
          ...(p?.history || []),
        ],
      }));
    }
    setBuying(false);
    setShowBuy(false);
    setSelected(null);
  };

  const usedPct = credits ? Math.min(100, Math.round((credits.used / credits.total) * 100)) : 0;
  const variant  = usedPct > 80 ? "danger" : usedPct > 60 ? "warning" : "success";

  if (loading) return <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>;

  return (
    <div>
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="fw-bold text-dark mb-0">
              <FaCoins className="text-warning me-2" />Credits
            </h3>
            <p className="text-muted mb-0 mt-1">Manage your engagement message credits</p>
          </div>
          <Button variant="primary" className="px-4" onClick={() => setShowBuy(true)}>
            <FaShoppingCart className="me-2" />Buy Credits
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <Row className="g-3 mb-4">
        {[
          { label: "Available Balance", value: credits?.balance?.toLocaleString() || 0, variant: "primary",   icon: "fa-coins" },
          { label: "Total Used",        value: credits?.used?.toLocaleString()    || 0, variant: "danger",    icon: "fa-arrow-down" },
          { label: "Total Purchased",   value: credits?.total?.toLocaleString()   || 0, variant: "success",   icon: "fa-arrow-up" },
        ].map((s, i) => (
          <Col md={4} key={i}>
            <Card className="shadow-sm border-0 h-100">
              <Card.Body className="p-4">
                <div className="d-flex align-items-center mb-2">
                  <div className={`bg-${s.variant} bg-opacity-10 rounded p-2 me-3`}>
                    <i className={`fas ${s.icon} text-${s.variant}`}></i>
                  </div>
                  <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.04em" }}>
                    {s.label}
                  </small>
                </div>
                <h3 className={`fw-bold text-${s.variant} mb-0`}>{s.value}</h3>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Usage Bar */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="fw-bold mb-0">Usage</h6>
            <small className="text-muted">{credits?.used?.toLocaleString()} / {credits?.total?.toLocaleString()} credits</small>
          </div>
          <ProgressBar now={usedPct} variant={variant} style={{ height: 10, borderRadius: 6 }} />
          <div className="d-flex justify-content-between mt-1">
            <small className="text-muted">{usedPct}% used</small>
            {usedPct > 80 && <small className="text-danger fw-semibold"><i className="fas fa-exclamation-circle me-1"></i>Running low — top up soon</small>}
          </div>
        </Card.Body>
      </Card>

      {/* History */}
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white border-bottom py-3">
          <h6 className="fw-bold mb-0"><FaHistory className="text-primary me-2" />Credit History</h6>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  {["Date","Note","Type","Credits"].map((h,i) => (
                    <th key={h} className={`py-3 border-0 fw-semibold text-muted ${i===0?"ps-4":""} ${i===2?"text-center":""} ${i===3?"text-end pe-4":""}`}
                      style={{ fontSize: "0.72rem", textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(credits?.history || []).map((h, i) => (
                  <tr key={i} className="align-middle">
                    <td className="ps-4 text-muted">{dayjs(h.date).format("DD MMM YYYY")}</td>
                    <td>{h.note}</td>
                    <td className="text-center">
                      <Badge bg={h.type === "purchase" ? "success" : "danger"} pill>{h.type}</Badge>
                    </td>
                    <td className={`text-end pe-4 fw-bold ${h.amount > 0 ? "text-success" : "text-danger"}`}>
                      {h.amount > 0 ? "+" : ""}{h.amount.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Buy Credits Modal */}
      <Modal show={showBuy} onHide={() => setShowBuy(false)} centered size="lg" aria-label="Buy Credits">
        <Modal.Header closeButton>
          <Modal.Title><FaShoppingCart className="me-2" />Buy Credits</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="text-muted mb-4">
            Credits are used for sending WhatsApp, SMS, and Email messages.
            <strong> 1 credit = 1 message.</strong> Credits never expire.
          </p>
          <Row className="g-3">
            {PACKS.map((pack) => (
              <Col md={6} key={pack.id}>
                <div
                  className={`p-4 rounded border h-100 ${selected?.id === pack.id ? "border-primary" : ""}`}
                  style={{
                    cursor: "pointer",
                    background: selected?.id === pack.id ? "#f0f4ff" : "#fff",
                    transition: "all 0.15s",
                    position: "relative",
                  }}
                  onClick={() => setSelected(pack)}
                  role="button"
                  aria-pressed={selected?.id === pack.id}
                  aria-label={`${pack.name} pack`}
                >
                  {pack.popular && (
                    <Badge bg="primary" style={{ position: "absolute", top: 12, right: 12 }}>Most Popular</Badge>
                  )}
                  <div className="fw-bold text-dark mb-1">{pack.name} Pack</div>
                  <div className="text-primary fw-bold" style={{ fontSize: "1.6rem" }}>
                    {pack.credits.toLocaleString()}
                  </div>
                  <div className="text-muted small mb-2">credits</div>
                  <div className="text-muted small mb-2">{pack.desc}</div>
                  <div className="fw-bold text-dark">₹{pack.price.toLocaleString()}</div>
                  <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                    ₹{(pack.price / pack.credits).toFixed(2)} per credit
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBuy(false)}>Cancel</Button>
          <Button variant="primary" onClick={buy} disabled={!selected || buying} className="px-4">
            {buying
              ? <><Spinner size="sm" animation="border" className="me-1" />Processing...</>
              : selected
                ? `Buy ${selected.credits.toLocaleString()} Credits — ₹${selected.price.toLocaleString()}`
                : "Select a Pack"}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .card { border-radius: 12px !important; }
        .card-header { border-radius: 12px 12px 0 0 !important; }
        .page-header { margin-bottom: 1.5rem; }
      `}</style>
    </div>
  );
};

export default CreditsManager;
